/**
 * DriverAvailabilityModal - Update driver operational availability
 *
 * Allows managers to set driver availability status with:
 * - Status (Available, Unavailable Planned, Unavailable Unplanned, Unknown)
 * - Reason (Leave, Sick Leave, Training, Medical Visit, etc.)
 * - Optional time window (from/until)
 * - Short operational note (120 chars max)
 *
 * This is operational availability only - NOT HR/payroll.
 */
import React, { useState, useEffect } from 'react';
import { X, UserCheck, UserMinus, UserX, HelpCircle, Calendar, Clock, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../../api.js';

// `label`/`description` = repli FR — l'affichage passe par t('drivers.availability.*')
const AVAILABILITY_STATUS_OPTIONS = [
  { value: 'AVAILABLE', label: 'Disponible', icon: UserCheck, color: 'green', description: 'Prêt à être assigné' },
  { value: 'UNAVAILABLE_PLANNED', label: 'Indisponible (prévu)', icon: UserMinus, color: 'yellow', description: 'Absence anticipée (congé, formation...)' },
  { value: 'UNAVAILABLE_UNPLANNED', label: 'Indisponible (imprévu)', icon: UserX, color: 'red', description: 'Absence non planifiée' },
  { value: 'UNKNOWN', label: 'Inconnu', icon: HelpCircle, color: 'gray', description: 'Statut à vérifier' }
];

const AVAILABILITY_REASON_OPTIONS = [
  { value: 'NONE', label: '— Aucun —', forStatus: ['AVAILABLE'] },
  { value: 'LEAVE', label: 'Congé', forStatus: ['UNAVAILABLE_PLANNED'] },
  { value: 'SICK_LEAVE', label: 'Arrêt maladie', forStatus: ['UNAVAILABLE_UNPLANNED', 'UNAVAILABLE_PLANNED'] },
  { value: 'TRAINING', label: 'Formation', forStatus: ['UNAVAILABLE_PLANNED'] },
  { value: 'MEDICAL_VISIT', label: 'Visite médicale', forStatus: ['UNAVAILABLE_PLANNED'] },
  { value: 'ADMIN', label: 'Administratif', forStatus: ['UNAVAILABLE_PLANNED', 'UNAVAILABLE_UNPLANNED'] },
  { value: 'VEHICLE_ISSUE', label: 'Problème véhicule', forStatus: ['UNAVAILABLE_UNPLANNED', 'UNAVAILABLE_PLANNED'] },
  { value: 'OTHER', label: 'Autre', forStatus: ['UNAVAILABLE_PLANNED', 'UNAVAILABLE_UNPLANNED', 'UNKNOWN'] }
];

function DriverAvailabilityModal({ isOpen, onClose, driver, onSuccess, onMessage }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('AVAILABLE');
  const [reason, setReason] = useState('NONE');
  const [note, setNote] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [untilDate, setUntilDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Initialize form with driver data
  useEffect(() => {
    if (driver && isOpen) {
      setStatus(driver.availability_status || 'UNKNOWN');
      setReason(driver.availability_reason || 'NONE');
      setNote(driver.availability_note || '');
      setFromDate(driver.availability_from ? driver.availability_from.split('T')[0] : '');
      setUntilDate(driver.availability_until ? driver.availability_until.split('T')[0] : '');
      setError('');
    }
  }, [driver, isOpen]);

  // Auto-set reason to NONE when status is AVAILABLE
  useEffect(() => {
    if (status === 'AVAILABLE') {
      setReason('NONE');
    } else if (reason === 'NONE' && status !== 'AVAILABLE') {
      // Auto-select first valid reason for the new status
      const validReasons = AVAILABILITY_REASON_OPTIONS.filter(r => r.forStatus.includes(status));
      if (validReasons.length > 0 && validReasons[0].value !== 'NONE') {
        setReason(validReasons[0].value);
      }
    }
  }, [status, reason]);

  // Get valid reasons for current status
  const validReasons = AVAILABILITY_REASON_OPTIONS.filter(
    r => r.forStatus.includes(status) || (status === 'AVAILABLE' && r.value === 'NONE')
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate
    if (note.length > 120) {
      setError(t('drivers.availabilityModal.noteTooLong', { max: 120 }));
      return;
    }

    if (fromDate && untilDate && new Date(untilDate) < new Date(fromDate)) {
      setError(t('drivers.availabilityModal.endBeforeStart'));
      return;
    }

    setSaving(true);

    try {
      const payload = {
        availability_status: status,
        availability_reason: reason,
        availability_note: note || null,
        availability_from: fromDate || null,
        availability_until: untilDate || null
      };

      await api.driver.updateAvailability(driver.id, payload);

      onMessage && onMessage({
        title: t('drivers.messages.successTitle'),
        message: t('drivers.availabilityModal.updated', { name: `${driver.first_name} ${driver.last_name}` }),
        type: 'success'
      });

      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to update availability:', err);
      setError(err.message || t('drivers.availabilityModal.updateError'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !driver) return null;

  const selectedStatusOption = AVAILABILITY_STATUS_OPTIONS.find(o => o.value === status);
  const StatusIcon = selectedStatusOption?.icon || HelpCircle;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('drivers.columns.availability')}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {driver.first_name} {driver.last_name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Info banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <strong>{t('drivers.availabilityModal.operationalOnly')}</strong>
              <br />
              {t('drivers.availabilityModal.noSensitiveData')}
            </div>
          </div>

          {/* Status Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">{t('drivers.columns.status')}</label>
            <div className="grid grid-cols-2 gap-3">
              {AVAILABILITY_STATUS_OPTIONS.map(option => {
                const Icon = option.icon;
                const isSelected = status === option.value;
                const colorClasses = {
                  green: isSelected ? 'bg-green-100 border-green-500 text-green-800' : 'hover:bg-green-50',
                  yellow: isSelected ? 'bg-yellow-100 border-yellow-500 text-yellow-800' : 'hover:bg-yellow-50',
                  red: isSelected ? 'bg-red-100 border-red-500 text-red-800' : 'hover:bg-red-50',
                  gray: isSelected ? 'bg-gray-100 border-gray-500 text-gray-800' : 'hover:bg-gray-50'
                };

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatus(option.value)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      isSelected ? colorClasses[option.color] : `border-gray-200 ${colorClasses[option.color]}`
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-5 h-5 ${isSelected ? '' : 'text-gray-400'}`} />
                      <span className="font-medium">{t(`drivers.availability.status.${option.value}`, { defaultValue: option.label })}</span>
                    </div>
                    <p className="text-xs text-gray-500">{t(`drivers.availability.description.${option.value}`, { defaultValue: option.description })}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reason Selection (only for unavailable statuses) */}
          {status !== 'AVAILABLE' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.detail.availability.reason')}</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                {validReasons.map(option => (
                  <option key={option.value} value={option.value}>
                    {t(`drivers.availability.reason.${option.value}`, { defaultValue: option.label })}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date Range (only for unavailable statuses) */}
          {status !== 'AVAILABLE' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  {t('drivers.availabilityModal.startOptional')}
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  {t('drivers.availabilityModal.endOptional')}
                </label>
                <input
                  type="date"
                  value={untilDate}
                  onChange={(e) => setUntilDate(e.target.value)}
                  min={fromDate || undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
          )}

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('drivers.availabilityModal.noteOptional')}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={120}
              rows={2}
              placeholder={t('drivers.availabilityModal.notePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
            />
            <div className="text-xs text-gray-400 text-right mt-1">
              {note.length}/120
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <StatusIcon className="w-4 h-4" />
              {saving ? t('common.states.saving') : t('common.actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DriverAvailabilityModal;
